import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { searchCowriterWorks } from '@App/api/routes/search'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import { SearchResultPartyDetails } from '@Entities/party-details'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { CowriterSearch } from '@App/stores/search/models/cowriter-search'
import { searchCowriters } from '@App/api/routes/songwriter/cowriter'
import { formatName } from '@App/helpers/format-name'
import { ResultPage } from '@Entities/result-page'
import { SearchPartyStore } from '@App/stores/search/party/search-party-store'
import { WriterModel } from '@Entities/work/writer-model'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import { ALPHABETICAL_WORK_TITLE_SORT } from '@App/const/pagination'

export const defaultPagination: PaginationInfo = {
    lastPage: 0,
    cappedLastPage: undefined,
    currentPage: 1,
    refined: false,
}

const SEARCH_COWRITER_OPTIONS: Array<SelectOptionHide<CowriterSearch>> = [
    { id: uuidv4(), value: 'writerName', label: 'Co-Writer Name', isPrimary: true },
    { id: uuidv4(), value: 'publisherName', label: 'Publisher Name', isPrimary: false },
    { id: uuidv4(), value: 'recordedBy', label: 'Recorded By', isPrimary: false },
    { id: uuidv4(), value: 'recordingName', label: 'Recording Name', isPrimary: false },
]
const defaultCowriterSelectionCriteria: Array<SearchCriterion<CowriterSearch>> = [
    {
        id: SEARCH_COWRITER_OPTIONS[0].id,
        selectedOption: SEARCH_COWRITER_OPTIONS[0],
        searchTerm: '',
        isFixed: true,
    },
]

export default class CowriterSearchPartyStore
    implements SearchPartyStore<SearchResultPartyDetails>, HasSearchCriteria<CowriterSearch>
{
    createWriterSearchCriteriaStore(): SearchCriteriaStore<CowriterSearch> {
        const writerSearchMaxCriteria = 4
        return new SearchCriteriaStore(
            defaultCowriterSelectionCriteria,
            SEARCH_COWRITER_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.primaryFilter,
            writerSearchMaxCriteria
        )
    }

    protected rootStore: RootStore

    private _publicSearchByPartySearchQuery: CowriterSearch = {
        writerName: '',
        publisherName: '',
        recordedBy: '',
        recordingName: '',
    }

    protected _searchResults: SearchResultPartyDetails[]

    protected _searchByCriteriaPaginationInfo: PaginationInfo

    private _secondarySearchPaginationInfo: PaginationInfo = defaultPagination

    protected _resultsPerPageLimit: number

    private _secondaryResultsPerPageLimit: number

    protected _currentSearchCriteriaStore: SearchCriteriaStore<CowriterSearch>

    private selectedWriterName: string

    private selectedWriterIpId: string | undefined

    protected _totalNumberOfResults: number

    private _secondarySearchResults: Works | undefined

    private _showWorks = false

    readonly displayType: 'songwriter' | 'public' = 'public'

    readonly canAddCriteria = true

    constructor(rootStore: RootStore) {
        makeObservable<
            CowriterSearchPartyStore,
            | '_searchResults'
            | '_totalNumberOfResults'
            | '_secondarySearchResults'
            | '_showWorks'
            | '_secondarySearchPaginationInfo'
            | '_currentSearchCriteriaStore'
            | '_searchByCriteriaPaginationInfo'
            | '_resultsPerPageLimit'
            | 'selectedWriterName'
            | 'resetWorks'
            | 'resetWritersAndWorks'
            | 'setPageLimit'
        >(this, {
            _searchResults: observable,
            _totalNumberOfResults: observable,
            _secondarySearchResults: observable,
            _showWorks: observable,
            _currentSearchCriteriaStore: observable,
            _searchByCriteriaPaginationInfo: observable,
            _resultsPerPageLimit: observable,
            _secondarySearchPaginationInfo: observable,
            selectedWriterName: observable,
            numTitles: computed,
            secondaryNumTitles: computed,
            worksResultMessage: computed,
            setCurrentPaginationPage: action,
            setQuery: action,
            setPageLimit: action,
            setShowWorks: action,
            setSelectedWriterName: action,
            resetStore: action,
            resetWorks: action,
            resetWritersAndWorks: action,
        })

        this.rootStore = rootStore
        this.resetStore()
    }

    get searchResults(): SearchResultPartyDetails[] {
        return this._searchResults
    }

    get searchByCriteriaPaginationInfo(): PaginationInfo | undefined {
        return this._searchByCriteriaPaginationInfo
    }

    get secondarySearchPaginationInfo(): PaginationInfo {
        return this._secondarySearchPaginationInfo
    }

    get resultsPerPageLimit(): number {
        return this._resultsPerPageLimit
    }

    get secondaryResultsPerPageLimit(): number {
        return this._secondaryResultsPerPageLimit
    }

    get secondarySearchResults(): Works | undefined {
        return this._secondarySearchResults
    }

    get showWorks(): boolean {
        return this._showWorks
    }

    get totalNumberOfResults(): number {
        return this._totalNumberOfResults
    }

    get numTitles(): INumTitles {
        return getNumTitles(
            this._totalNumberOfResults,
            this._searchByCriteriaPaginationInfo.currentPage ?? 1,
            this._resultsPerPageLimit
        )
    }

    get secondaryNumTitles(): INumTitles {
        return getNumTitles(
            this._secondarySearchResults?.count ?? 0,
            this._secondarySearchPaginationInfo?.currentPage ?? 1,
            this._secondaryResultsPerPageLimit
        )
    }

    get worksResultMessage(): string {
        if (this.selectedWriterName) {
            return `Works for ${this.selectedWriterName}`
        }
        return 'Works'
    }

    get searchCriteriaStore(): SearchCriteriaStore<CowriterSearch> {
        return this._currentSearchCriteriaStore
    }

    setsecondarySearchResults(works: Works) {
        this._secondarySearchResults = works
    }

    setCurrentPaginationPage(page: number) {
        this._searchByCriteriaPaginationInfo.currentPage = page ?? 1
    }

    async secondarySearch(writer: SearchResultPartyDetails) {
        this.setSelectedWriterIpId(writer.ipId ?? '')
        this.setSelectedWriterName(writer.name)
        const searchWorksQuery = {
            writerIpIds: writer.ipId ?? '',
        }
        this.searchCowriterWorks(searchWorksQuery, this._secondarySearchPaginationInfo ?? defaultPagination).then(
            (result) => {
                runInAction(() => {
                    this.setsecondarySearchResults(result)
                    this.setShowWorks(true)
                })
            }
        )
    }

    setSelectedWriterName(name: string) {
        this.selectedWriterName = name
    }

    setSelectedWriterIpId(ipId: string) {
        this.selectedWriterIpId = ipId
    }

    setQuery(search: CowriterSearch): void {
        this._publicSearchByPartySearchQuery = search
    }

    async setPageLimit(newLimit: number): Promise<void> {
        this._resultsPerPageLimit = newLimit
        await this.search()
    }

    setShowWorks(showWritersWorks: boolean): void {
        this._showWorks = showWritersWorks
    }

    async search() {
        await this.getCowriters(this._publicSearchByPartySearchQuery)
    }

    async onChangeSecondarySearchPage(paginationBody: PaginationBody): Promise<void> {
        if (this.selectedWriterIpId) {
            this.resetWorks()
            const search: WorkSearch = {
                writerIpIds: this.selectedWriterIpId,
            }
            try {
                const response = await this.searchCowriterWorks(search, paginationBody)

                runInAction(() => {
                    this._secondarySearchResults = response
                    if (paginationBody.limit && paginationBody.page && response) {
                        this._secondarySearchPaginationInfo = calcNumPages(
                            response.count,
                            paginationBody.limit,
                            paginationBody.page ?? 1,
                            paginationBody.refined
                        )
                    }
                })
            } catch (e) {
                this.rootStore.popupStore.setAlertFromError(e)
            }
        }
    }

    private searchCowriterWorks = async (search: WorkSearch, pagination: PaginationBody) => {
        this.rootStore.preloaderStore.openTransparentPreloader(true)
        const sortedPagination = {
            sort: ALPHABETICAL_WORK_TITLE_SORT,
            ...pagination,
        }
        const apiResponse = await searchCowriterWorks(
            this.rootStore.songwriterStore.currentSongwriterId,
            sortedPagination,
            search
        )
        runInAction(() => {
            this._secondarySearchPaginationInfo = calcNumPages(
                apiResponse.data.totalElements,
                pagination.limit,
                pagination.page
            )
        })
        this.rootStore.preloaderStore.openTransparentPreloader(false)
        return {
            count: apiResponse.data.totalElements,
            works: apiResponse.data.content,
        }
    }

    async secondarySetPageLimit(newLimit: number): Promise<void> {
        this._secondaryResultsPerPageLimit = newLimit
        const pagination: PaginationBody = { page: 1, limit: newLimit }
        await this.onChangeSecondarySearchPage(pagination)
    }

    resetStore(): void {
        this._resultsPerPageLimit = 10
        this.resetWritersAndWorks()
        this.resetSearchCriteriaStore()
        this._publicSearchByPartySearchQuery = {
            writerName: '',
            publisherName: '',
            recordedBy: '',
            recordingName: '',
        }
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createWriterSearchCriteriaStore()
    }

    private resetWorks(): void {
        this._secondarySearchResults = undefined
        this._secondarySearchPaginationInfo = defaultPagination
    }

    protected resetWritersAndWorks(): void {
        this.resetSearchCriteriaStore()
        this._showWorks = false
        this._searchResults = []
        this._searchByCriteriaPaginationInfo = defaultPagination
        this._totalNumberOfResults = 0
        this.selectedWriterName = ''
        this.selectedWriterIpId = undefined
        this._secondaryResultsPerPageLimit = 10

        this.resetWorks()
    }

    private async getCowritersApiCall(
        songwriterId: string,
        pagination: PaginationBody,
        search: CowriterSearch
    ): Promise<ResultPage<WriterModel>> {
        return searchCowriters(songwriterId, pagination, search).then((response) => response.data)
    }

    private async getCowriters(search: CowriterSearch): Promise<void> {
        try {
            const response = await this.getCowritersApiCall(
                this.rootStore.songwriterStore.currentSongwriterId,
                {
                    page: this._searchByCriteriaPaginationInfo.currentPage ?? 1,
                    limit: this._resultsPerPageLimit,
                },
                search
            )
            runInAction(() => {
                this._totalNumberOfResults = response.totalElements
                this._searchByCriteriaPaginationInfo = calcNumPages(
                    this._totalNumberOfResults,
                    this._resultsPerPageLimit,
                    this._searchByCriteriaPaginationInfo.currentPage ?? 1
                )
                this._searchResults = response.content.map((writer) => ({
                    ipId: writer.ipId,
                    ipiNumber: writer.ipiNumber,
                    worksCount: writer.worksCount,
                    name: formatName(writer.firstName, writer.lastName) ?? '',
                }))
            })
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }
}
